<template lang="pug">
  v-flex(xs12 md4)
    v-layout(row wrap)
      v-flex(xs12)
        div.headline.pb-5 Set the Timing
      v-flex(xs12 data-cy="set-action-timing")
        v-select(
          :items="Object.values(timingTypes)"
          item-disabled="test()"
          v-model="selectedTimingType"
          item-text="name"
          return-object
        )
        div.caption.tooltip(v-if="timingTypeIsExactDateTime(selectedTimingType)")
          p
            v-icon.pr-1(small) info
            | We are working on implementing this timing type. In the mean time, you can:
            ul
              li Edit this action and select one of the supported types
              li Select another campaign

        v-text-field(
          v-if="timingTypeIsDelayByDays(selectedTimingType)"
          v-model="delayInDays"
          type="number"
          min="1"
          :max="maximumDelayLimit"
          :label="delayDaysLabel"
          :error-messages="errorMessage"
        )
        v-combobox(
          v-if="timingTypeIsDelayByDays(selectedTimingType)"
          v-model="dispatchTime"
          label="Time"
          prepend-icon="access_time"
          :items="timeSlots()"
          :rules='timeRules'
          @keyup="onTimeChange"
        )
        v-text-field(
          v-if="timingTypeIsDelayByMinutes(selectedTimingType)"
          v-model="delayInMinutes"
          type="number"
          min="1"
          :max="maximumDelayLimit"
          label="Delay in Minutes"
          :error-messages="errorMessage"
        )

        v-flex(xs12 v-if="timingTypeIsRecurring(selectedTimingType)")
          span.pr-1 First task starts
          v-layout(align-center wrap)
            v-flex(xs4 v-if="firstTaskStarts == 'after'")
              v-text-field(
                v-model="delayInDays"
                type="number"
                :error-messages="errorMessage"
                min="1"
                suffix="day(s)"
              )
            v-flex(xs5)
              v-radio-group(v-model="firstTaskStarts" row)
                v-spacer
                v-radio(:key="1" value="on" label="On")
                v-radio(:key="2" value="after" label="After")
                v-spacer

          v-select(
            :items="Object.values(startEventTriggersOptions)"
            item-disabled="test()"
            v-model="actionTrigger"
            item-text="name"
            return-object
          )
          campaign-recurrence-picker(
            v-if="timingTypeIsRecurring(selectedTimingType)"
            v-model="sendRecurring"
            :initialRule="recurrence_rule"
            isCampaignSetup
          )
          v-combobox(
            v-model="dispatchTime"
            label="Time"
            prepend-icon="access_time"
            :items="timeSlots()"
            :rules='timeRules'
            @keyup="onTimeChange"
          )

        v-checkbox(
          v-if="isStopOnTextReplyVisible"
          v-model="stopOnTextReply"
          color="primary"
        )
          template(slot="label")
            span.pr-1 Stop on Text Reply
            v-tooltip(bottom)
              template(slot="activator")
                v-icon help_outline
              span Campaign actions will automatically stop after a text reply from a contact is received.
              br
              span You must have a LionDesk custom phone number to use this feature

</template>
<script>
import { createNamespacedHelpers } from 'vuex';
import dateFns from 'date-fns';
import { convertTime12 } from '@/Core/filters/dates';
import { timeSlots, timeRules } from '@/Core/utilities';
import { secondsToMinutes, minutesToSeconds } from '@/Core/filters/times';
import { TIMING_TYPES, START_EVENTS_TRIGGERS, getSartEventTriggersByField } from '../config/actions';
import CampaignRecurrencePicker from './CampaignRecurrencePicker';
const { mapState: mapUserInformationState } = createNamespacedHelpers('userInformation');

const MAXIMUM_DELAY_LIMIT = 9999;
const INITIAL_RECURRENCE = "FREQ=DAILY;INTERVAL=1;COUNT=5"

export default {
  data() {
    const initialEventTrigger = getSartEventTriggersByField(this.value.event_trigger_type, "key")
    let firstTaskStarts = "on"
    if (this.value.delay_by_days > 0) {
      firstTaskStarts = "after"
    }
    const defaultSendRecurring = {
      name: null,
      recurrenceRule: this.value.recurrence_rule || INITIAL_RECURRENCE,
      startDate: new Date(),
    }
    return {
      delayInDays: this.value.delay_by_days || 1,
      delayInMinutes: 1,
      errorMessage: '',
      // The current time selected, used in combination with date.
      dispatchTime: this.getInitialTime(),
      timeRules,
      timeSlots,
      selectedTimingType: '',
      stopOnTextReply: this.value.stop_on_text_reply || false,
      maximumDelayLimit: MAXIMUM_DELAY_LIMIT,
      sendRecurring: defaultSendRecurring,
      initialDateParsed: dateFns.parse(new Date()),
      recurrence_rule: this.value.recurrence_rule || INITIAL_RECURRENCE,
      startEventTriggersOptions: START_EVENTS_TRIGGERS,
      actionTrigger: initialEventTrigger || START_EVENTS_TRIGGERS.CAMPAIGN_STARTS,
      firstTaskStarts: firstTaskStarts
    };
  },
  components: {
    CampaignRecurrencePicker,
  },

  props: {
    /**
     * Array of keys that adds a timing type to the list dropdown even if it's disabled
     * Defaults to none
     */
    initialTiming: Object,
    /**
     * Controls whether the text unsubscribe checkbox is displayed
     */
    isStopOnTextReplyVisible: Boolean,
    /**
     * The default prop passed in, expects the delay_by_days and dispatch_time
     * e.g. {
     *  delay_by_days: 1,
     *  dispatch_time: 19:00:00
     * }
     */
    value: Object,

    // Selected action type
    actionType: Object,
  },

  computed: {
    ...mapUserInformationState(['user']),
    timingTypes: {
      get() {
        const supportedTypes = Object.values(TIMING_TYPES)
          .filter(type => {
            if (type && type.key === TIMING_TYPES.RECURRING.key) {
              return type.supported && this.actionType.key === "task"
            }
            return type.supported
          });

        if (this.initialTiming.timing_type) supportedTypes.push(this.initialTiming.timing_type);

        return supportedTypes;
      },
    },
    delayDaysLabel() {
      return this.value.timing_type === TIMING_TYPES.RECURRING ? "Delay in Days for the first trigger" : "Delay in Days"
    },
  },

  mounted() {
    /**
     * It would be more intuitive if the curent default selection is pushed
     * as the desired time, rather than the client having to toggle a value
     * to trigger the watcher
     */
    this.selectedTimingType =
      (this.value && this.value.timing_type) ?
        this.value.timing_type :
        TIMING_TYPES.IMMEDIATELY;
    // Assigns delay in minutes if present
    if (this.value && this.value.delay_by_seconds) {
      this.delayInMinutes = secondsToMinutes(this.value.delay_by_seconds);
    }
  },

  watch: {
    delayInDays(value) {
      this.emitActionTiming();
    },
    delayInMinutes(value) {
      this.emitActionTiming();
    },
    dispatchTime(value) {
      this.emitActionTiming();
    },
    stopOnTextReply(value) {
      this.emitActionTiming();
    },
    selectedTimingType(value) {
      this.emitActionTiming();
    },
    actionTrigger() {
      this.emitActionTiming();
    },
    firstTaskStarts() {
      this.emitActionTiming();
    },
    /*
     * Callback to set the necessary details on recurrence.
     */
    sendRecurring: {
      handler(value) {
        this.emitActionTiming();
      }
    },
  },

  methods: {
    timingTypeIsImmediately(selectedTimingType) {
      return selectedTimingType.key === TIMING_TYPES.IMMEDIATELY.key;
    },
    timingTypeIsDelayByDays(selectedTimingType) {
      return selectedTimingType.key === TIMING_TYPES.DELAY_BY_DAYS.key;
    },
    timingTypeIsExactDateTime(selectedTimingType) {
      return selectedTimingType.key === TIMING_TYPES.EXACT_DATE_TIME.key;
    },
    timingTypeIsDelayByMinutes(selectedTimingType) {
      return selectedTimingType.key === TIMING_TYPES.DELAY_BY_MINUTES.key;
    },
    timingTypeIsRecurring(selectedTimingType) {
      return selectedTimingType.key === TIMING_TYPES.RECURRING.key;
    },
    /**
     * Emits changes in this component back to its parent
     */
    emitActionTiming() {
      this.errorMessage = '';
      this.$emit('set-validation', true);

      switch (this.selectedTimingType.key) {

        case TIMING_TYPES.IMMEDIATELY.key:
          this.$emit('input', {
            timing_type: this.selectedTimingType,
            stop_on_text_reply: this.stopOnTextReply,
            delay_by_days: null,
            dispatch_time: null,
          });
          break;

        case TIMING_TYPES.DELAY_BY_DAYS.key: {
          // Gets the error messages
          // Checks if the value is a valid number
          const errorMsg = this.checkNumberError(this.delayInDays);

          // If there is an error, displays the error and emit an error
          if (errorMsg) {
            this.$emit('set-validation', false);
            this.errorMessage = errorMsg;

            // If everything went smoothly (time is valid)
          } else if (this.isTimeValid(this.dispatchTime)) {
            this.errorMessage = '';
            this.$emit('set-validation', true);

            const delay = parseInt(this.delayInDays, 10);

            this.$emit('input', {
              timing_type: this.selectedTimingType,
              stop_on_text_reply: this.stopOnTextReply,
              delay_by_days: delay || 1,
              dispatch_time: this.dispatchTime,
            });
          }
          break;
        }

        case TIMING_TYPES.DELAY_BY_MINUTES.key: {
          // Gets the error messages
          // Checks if the value is a valid number
          const errorMsg = this.checkNumberError(this.delayInMinutes);

          // If there is an error, display it and emit the error
          if (errorMsg) {
            this.$emit('set-validation', false);
            this.errorMessage = errorMsg;

            // If there is no error, proceed with emitting the desired timing
          } else {
            this.errorMessage = '';
            this.$emit('set-validation', true);

            const delay = minutesToSeconds(this.delayInMinutes);

            this.$emit('input', {
              timing_type: this.selectedTimingType,
              stop_on_text_reply: this.stopOnTextReply,
              delay_by_seconds: delay,
            });
          }
          break;
        }

        case TIMING_TYPES.EXACT_DATE_TIME.key: {
          if (this.initialTiming.timing_type.key === this.selectedTimingType.key) {
            this.$emit('input', {
              timing_type: this.selectedTimingType,
              stop_on_text_reply: this.stopOnTextReply,
              dispatch_datetime: this.initialTiming.dispatch_datetime,
            });
          }
          break;
        }

        case TIMING_TYPES.RECURRING.key: {
          let delay = 0
          if (this.firstTaskStarts === "after") {
            // Gets the error messages
            // Checks if the value is a valid number
            const errorMsg = this.checkNumberError(this.delayInDays);

            if (errorMsg) {
              this.$emit('set-validation', false);
              this.errorMessage = errorMsg;
              break;
            }
            delay = parseInt(this.delayInDays, 10);
          }

          if (this.isTimeValid(this.dispatchTime)) {
            this.errorMessage = '';
            this.$emit('set-validation', true);

            const input = {
              timing_type: this.selectedTimingType,
              recurrence_rule: this.sendRecurring.recurrenceRule,
              delay_by_days: delay,
              dispatch_time: this.dispatchTime,
              event_trigger_type: this.actionTrigger.key,
              first_task_starts: this.firstTaskStarts,
            }
            this.$emit('input', input);
          }
          break;
        }
        default: break;
      }
    },
    /**
     * Parses the given initial time if exists. Otherwise default to whatever the current time is
     */
    getInitialTime() {
      if (this.value && this.value.dispatch_time) {
        return this.value.dispatch_time;
      }
      return convertTime12(new Date()); // The current time selected in 12-hour format
    },
    // Helper function to check if the value is a number greater than zero
    checkNumberError(value) {
      if (Number.isNaN(value)) return 'Please enter a valid number';
      if (value > MAXIMUM_DELAY_LIMIT) return 'Maximum limit on delay exceeded';
      if (value <= 0) return 'Delay must be a number greater than 0';
      return '';
    },
    // Helper function to check whether the value is a valid 24-hour-format time
    isTimeValid(value) {
      return timeRules[0](value) === true;
    },
    onTimeChange(event) {
      if (event && event.target && event.target.value) {
        this.dispatchTime = event.target.value;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.tooltip {
  color: var(--v-secondary-lighten3);
}
</style>
